import { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { canAccessRoute } from '../utils/routePermissions';
import { localizedErrorMessage } from '../utils/italianErrorMessage';

const PermissionsContext = createContext({
  loading: true, error: '', can: () => false, canVisit: () => false,
});

export function PermissionsProvider({ children }) {
  const { accessToken, tenantId, tenantType, isAuthenticated } = useSelector((state) => state.auth);
  const { pathname } = useLocation();
  const key = `${tenantId}:${accessToken}`;
  const [result, setResult] = useState(null);
  const isTenantPage = !['/', '/login', '/reset-password', '/admin_page'].includes(pathname);

  useEffect(() => {
    if (!isAuthenticated || !accessToken || !tenantId || !isTenantPage) {
      setResult(null);
      return undefined;
    }
    let controller;
    const refresh = async () => {
      controller?.abort();
      controller = new AbortController();
      const { signal } = controller;
      try {
        // Never reuse offline permissions cached for another user or an old session.
        const response = await fetch('/api/users/me/permissions', {
          headers: { Authorization: `Bearer ${accessToken}`, 'x-tenant-id': tenantId },
          cache: 'no-store',
          signal,
        });
        if (!response.ok) throw new Error('Impossibile verificare i permessi. Riprova ad accedere.');
        const data = await response.json();
        if (!signal.aborted) setResult({ key, actions: data.effectivePermissions.map((permission) => permission.action) });
      } catch (error) {
        if (!signal.aborted) {
          setResult({ key, actions: [], error: localizedErrorMessage(error, 'Impossibile verificare i permessi.') });
        }
      }
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => {
      controller?.abort();
      window.removeEventListener('focus', refresh);
    };
  }, [accessToken, tenantId, isAuthenticated, isTenantPage, pathname, key]);

  const current = result?.key === key ? result : null;
  const actions = current?.actions || [];
  return (
    <PermissionsContext.Provider value={{
      loading: isTenantPage && !current,
      error: current?.error || '',
      can: (action) => actions.includes(action),
      canVisit: (path) => Boolean(current && !current.error && canAccessRoute(path, actions, tenantType)),
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionsContext);
